import React, { useContext, useEffect } from "react";
import { UnmountClosed } from "react-collapse";
import { IntlContext, useIntl } from "react-intl";

import { getBaggageDetails } from "shared/lib/functions";
import { useToggle } from "shared/lib/hooks";
import FlightCombination from "shared/types/flightCombination";

import Img from "@components/FileServer/Img";
import FormattedMessage from "@components/UI/FormattedMessage";

import { ReturnTypes } from "../../hooks/result/useShareInfo";
import ShareModal from "../UI/ShareModal";
import FlightAdditionalInfoBaggage from "./AdditionalInfoBaggageModal";
import FlightConditions from "./FlightConditions";

export type GetRefsForFlightOffer = () => string[];
type Page = string;
type Index = string | number;
type Style = any;
type ToggleAdditionalBaggageInfo = () => void;
type ToggleConditions = () => void;

interface FlightAdditionalInfoBaggageInfoProps {
  selectedCombination: FlightCombination;
  toggleAdditionalBaggageInfo: ToggleAdditionalBaggageInfo;
  index: Index;
  style?: Style;
}

const FlightAdditionalInfoBaggageInfo = ({
  selectedCombination,
  toggleAdditionalBaggageInfo,
  index,
  style,
}: FlightAdditionalInfoBaggageInfoProps) => {
  const intl = useIntl();

  const baggageLimit = selectedCombination.PricingDetail.BaggageLimit;

  const { baggageQuantity, baggageWeight } = getBaggageDetails(
    baggageLimit,
    intl
  );

  if (!baggageWeight && !baggageQuantity) return null;

  const baggageSelectionAvailable =
    baggageLimit.BaggageSelectionAvailable === "true";

  const baggageIncluded = baggageLimit.Included === "Yes";
  const baggageNotIncluded = baggageLimit.Included === "No";
  const baggageUnknown = baggageLimit.Included === "Unknown";

  const noBaggageAllowedNoBaggageSelection =
    baggageNotIncluded && !baggageSelectionAvailable;

  const baggageIcon = baggageIncluded
    ? "/static/images/ico-baggage.svg"
    : "/static/images/ico-no-baggage.svg";

  return (
    <div
      role="button"
      className="flight-additional-info-icon pointer"
      data-cy={`flight-additional-info-div-${index}`}
      onClick={toggleAdditionalBaggageInfo}
      style={style}
    >
      {(baggageIncluded || noBaggageAllowedNoBaggageSelection) && (
        <>
          <Img
            src={baggageIcon}
            className="flight-additional-info-icon-icon"
            alt="additional info icon"
          />
          <span
            className={
              baggageNotIncluded && !baggageSelectionAvailable
                ? "flight-additional-info-icon-number-not-allowed"
                : "flight-additional-info-icon-number"
            }
          >
            {baggageQuantity}
            {` `}
            {baggageWeight}
          </span>
        </>
      )}

      {baggageUnknown && baggageSelectionAvailable && (
        <>
          <Img
            src={baggageIcon}
            className="flight-additional-info-icon-icon"
            alt="additional info icon"
          />
          <span className="flight-additional-info-icon-number-not-allowed">
            <FormattedMessage id="FlightAdditionalInfoBaggage.chooseInNextStep" />
          </span>
        </>
      )}

      {baggageUnknown && !baggageSelectionAvailable && <></>}

      {baggageNotIncluded && baggageSelectionAvailable && (
        <>
          <Img
            src={baggageIcon}
            className="flight-additional-info-icon-icon"
            alt="additional info icon"
          />
          <span className="flight-additional-info-icon-number-not-allowed">
            <FormattedMessage id="FlightAdditionalInfoBaggage.chooseInNextStep" />
          </span>
        </>
      )}
    </div>
  );
};

interface FlightAdditionalInfoMobileProps {
  selectedCombination: FlightCombination;
  index: Index;
  toggleAdditionalBaggageInfo: ToggleAdditionalBaggageInfo;
  toggleConditions: ToggleConditions;
  showBaggageDetailLink: boolean;
  shareInfo: ReturnTypes;
  setIsSomeModalOpenned: React.Dispatch<React.SetStateAction<boolean>>;
}

const FlightAdditionalInfoMobile = ({
  selectedCombination,
  index,
  toggleAdditionalBaggageInfo,
  toggleConditions,
  showBaggageDetailLink,
  shareInfo,
  setIsSomeModalOpenned,
}: FlightAdditionalInfoMobileProps) => {
  return (
    <div className="flight-additional-info">
      <div className="flight-additional-info-mobile-links">
        <span
          role="button"
          className="link pointer"
          onClick={toggleConditions}
          data-test-id={`tariff-conditions-${index}`}
        >
          <FormattedMessage id="FlightAdditionalInfo.tariffConditions" />
        </span>
        {showBaggageDetailLink && (
          <span
            role="button"
            className="link pointer"
            onClick={toggleAdditionalBaggageInfo}
          >
            <FormattedMessage id="Result.baggageDetails" />
          </span>
        )}
        <ShareModal
          setIsSomeModalOpenned={setIsSomeModalOpenned}
          title={shareInfo.title}
          url={shareInfo.url}
          additionalInfo={shareInfo.aditionalInfo}
          className="link pointer"
          customStyle={{
            toggle: {
              textDecoration: "underline",
            },
          }}
        />
      </div>

      <FlightAdditionalInfoBaggageInfo
        selectedCombination={selectedCombination}
        style={{ width: "100%", textAlign: "center" }}
        index={null}
        toggleAdditionalBaggageInfo={toggleAdditionalBaggageInfo}
      />
    </div>
  );
};

interface FlightAdditionalInfoDesktopProps {
  selectedCombination: FlightCombination;
  index: Index;
  page: Page;
  toggleAdditionalBaggageInfo: ToggleAdditionalBaggageInfo;
  toggleConditions: ToggleConditions;
  showBaggageDetailLink: boolean;
  shareInfo: ReturnTypes;
  setIsSomeModalOpenned: React.Dispatch<React.SetStateAction<boolean>>;
}

const FlightAdditionalInfoDesktop = ({
  selectedCombination,
  index,
  page,
  toggleAdditionalBaggageInfo,
  toggleConditions,
  showBaggageDetailLink,
  shareInfo,
  setIsSomeModalOpenned,
}: FlightAdditionalInfoDesktopProps) => {
  let flightDetailDesktopLinksClass = "flight-detail-desktop-links";
  if (page === "results") {
    flightDetailDesktopLinksClass = "flight-detail-desktop-links-results";
  }

  return (
    <div className="flight-additional-info flight-additional-info-desktop">
      <div className="flight-additional-info-desktop-basics">
        <FlightAdditionalInfoBaggageInfo
          selectedCombination={selectedCombination}
          index={index}
          toggleAdditionalBaggageInfo={toggleAdditionalBaggageInfo}
        />
      </div>

      <div className={flightDetailDesktopLinksClass}>
        <span
          role="button"
          data-cy={`link-show-fare-conditions-${index}`}
          className="link pointer"
          onClick={toggleConditions}
        >
          <FormattedMessage id="FlightAdditionalInfo.tariffConditions" />
        </span>
        {showBaggageDetailLink && (
          <>
            <span className="text-separator"> | </span>
            <span
              role="button"
              className="link pointer link-show-baggage-info"
              data-cy={`link-show-baggage-info-${index}`}
              onClick={toggleAdditionalBaggageInfo}
            >
              <FormattedMessage id="Result.baggageDetails" />
            </span>
          </>
        )}
        <ShareModal
          setIsSomeModalOpenned={setIsSomeModalOpenned}
          title={shareInfo.title}
          url={shareInfo.url}
          additionalInfo={shareInfo.aditionalInfo}
          className="link pointer"
          showSeparator
        />
      </div>
    </div>
  );
};

type FlightAdditionalInfoProps = {
  selectedCombination: FlightCombination;
  page: Page;
  index: Index;
  getRefsForFlightOffer: GetRefsForFlightOffer;
  setIsSomeModalOpenned: React.Dispatch<React.SetStateAction<boolean>>;
  shareInfo: ReturnTypes;
};

const FlightAdditionalInfo = ({
  selectedCombination,
  page,
  getRefsForFlightOffer,
  index,
  shareInfo,
  setIsSomeModalOpenned,
}: FlightAdditionalInfoProps) => {
  const intl = useContext(IntlContext);
  const [showAdditionalBaggageInfo, toggleAdditionalBaggageInfo] = useToggle(
    false
  );
  const [isShowingConditions, toggleConditions] = useToggle(false);

  const baggageLimit = selectedCombination.PricingDetail.BaggageLimit;
  const showBaggageDetailLink =
    Boolean(baggageLimit.BaggageSelectionAvailable) ??
    baggageLimit.DetailsAvailable !== "false";

  useEffect(() => {
    if (setIsSomeModalOpenned) {
      setIsSomeModalOpenned(showAdditionalBaggageInfo);
    }
    // eslint-disable-next-line
  }, [showAdditionalBaggageInfo]);

  return (
    <div>
      <UnmountClosed isOpened={isShowingConditions}>
        <FlightConditions
          setIsSomeModalOpenned={setIsSomeModalOpenned}
          getRefsForFlightOffer={getRefsForFlightOffer}
          onClose={toggleConditions}
        />
      </UnmountClosed>
      {showAdditionalBaggageInfo && (
        <FlightAdditionalInfoBaggage
          getRefsForFlightOffer={getRefsForFlightOffer}
          onClose={toggleAdditionalBaggageInfo}
          intl={intl}
          baggageInfo={baggageLimit}
        />
      )}
      <div>
        <FlightAdditionalInfoMobile
          setIsSomeModalOpenned={setIsSomeModalOpenned}
          index={index}
          toggleAdditionalBaggageInfo={toggleAdditionalBaggageInfo}
          toggleConditions={toggleConditions}
          showBaggageDetailLink={showBaggageDetailLink}
          selectedCombination={selectedCombination}
          shareInfo={shareInfo}
        />
        <FlightAdditionalInfoDesktop
          setIsSomeModalOpenned={setIsSomeModalOpenned}
          index={index}
          page={page}
          toggleAdditionalBaggageInfo={toggleAdditionalBaggageInfo}
          toggleConditions={toggleConditions}
          showBaggageDetailLink={showBaggageDetailLink}
          selectedCombination={selectedCombination}
          shareInfo={shareInfo}
        />
      </div>
    </div>
  );
};

export default FlightAdditionalInfo;
